'use client'

import type { ButtonProps } from '@pearlconvert/ui/components/button/button.types'

import classNames from 'classnames'
import React from 'react'

import { ButtonContent } from '@pearlconvert/ui/components/button'
import { useButton } from '@pearlconvert/ui/components/button/batons/use-button'
import { Link } from '@pearlconvert/ui/components/link'

import '@pearlconvert/ui/components/button/styles/button.scss'

const Button: React.FC<ButtonProps> = (props: ButtonProps) => {
  const {
    children,
    title,
    ariaLabel,
    size = 'normal',
    type,
    role,
    isLoading = false,
    iconLeft,
    iconRight,
    disabled,
    inactive,
    className,
    isToggle = false,
    openNewTab = false,
    testId,
    url,
    scroll = true,
    download,
    buttonType,
    customStyle,
    onClick,
    onKeyDown,
    ref,
    ariaHaspopup,
    ariaExpanded,
    ariaHidden,
    ariaHiddenChild,
  } = props

  const buttonClass = classNames(
    'button',
    {
      [`button__${type}`]: type,
      [`button__${size}`]: size,
      button__disabled: disabled,
      button__loading: isLoading,
      [`button__iconLeft--${iconLeft}`]: iconLeft,
      [`button__iconRight--${iconRight}`]: iconRight,
      'button__button--has-icons': iconLeft || iconRight,
    },
    className
  )

  const { pressed, handleClick } = useButton({ onClick })

  return url ? (
    <Link
      href={url}
      scroll={scroll}
      target={openNewTab ? '_blank' : '_self'}
      className={buttonClass}
      aria-label={ariaLabel || title}
      {...(role && { role: role })}
      {...(ariaHaspopup !== undefined && { 'aria-haspopup': ariaHaspopup })}
      {...(ariaExpanded !== undefined && { 'aria-expanded': ariaExpanded })}
      {...(ariaHidden !== undefined && { 'aria-hidden': ariaHidden })}
      {...(download && { download, prefetch: false })}
      data-testid={testId}
      onClick={handleClick}
      onKeyDown={onKeyDown}
      ref={ref as React.RefObject<HTMLAnchorElement>}
    >
      <ButtonContent ariaHidden={ariaHiddenChild}>{children}</ButtonContent>
    </Link>
  ) : (
    <button
      title={title}
      className={buttonClass}
      disabled={disabled}
      type={buttonType}
      style={customStyle}
      aria-disabled={disabled || inactive}
      aria-label={ariaLabel || title}
      {...(role && { role: role })}
      {...(isToggle && { 'aria-pressed': pressed })}
      {...(ariaHaspopup !== undefined && { 'aria-haspopup': ariaHaspopup })}
      {...(ariaExpanded !== undefined && { 'aria-expanded': ariaExpanded })}
      {...(ariaHidden !== undefined && { 'aria-hidden': ariaHidden })}
      data-testid={testId}
      onClick={handleClick}
      onKeyDown={onKeyDown}
      ref={ref as React.RefObject<HTMLButtonElement>}
    >
      <ButtonContent ariaHidden={ariaHiddenChild}>{children}</ButtonContent>
    </button>
  )
}

export default Button
